import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Carousel from 'nuka-carousel';

import buildAffiliateLink from 'lib/buildAffiliateLink';
import { description as DescriptionPropType } from 'lib/CustomPropTypes';
import { getDateTime } from 'lib/DateTime';
import $t from 'lib/trackingService';
import { isTelemundoVertical } from 'lib/vertical';

import Picture from 'components/Picture';
import ShopButtonInfo from 'components/ShopButtonInfo';

import PromotionalMedia from './PromotionalMedia'; // eslint-disable-line
import AIMS_FLAVORS from '../../lib/aimsFlavors';

// TODO: [BENTO-12233] Temporarily leaving styles.module.scss to handle grid styles.
// Will need refactor when solution for grid is implemented within new theming strategy
import styles from './styles.module.scss';

const block = 'listicleProduct';

const ENCODER_STOP_DATE = 'Wed Apr 15 2020 00:00:00';

export const ListicleProductContainer = ({ children, isLarge }) => (
  <div
    className={classNames(
      `${block}`,
      'dn-print mv8',
      {
        [styles.gridContainerLarge]: isLarge,
        'listicle-product__grid-container--large': isLarge,
        'listicle-product__grid-container--small': !isLarge,
      },
    )}
    data-test="listicle-product-container"
  >
    <div className="listicle-product__grid-row">
      {children}
    </div>
  </div>
);

ListicleProductContainer.propTypes = {
  children: PropTypes.node,
  isLarge: PropTypes.bool,
};

ListicleProductContainer.defaultProps = {
  children: null,
  isLarge: false,
};

const findPrices = (offer) => offer.prices.reduce((accum, current) => ({
  ...accum,
  [current.type]: current.price,
}),
{});

// adding when page loads as lifecyle not used for this component
$t('register', ['mbt_ecommerce_shop_button']);


class ListicleProduct extends React.Component {
  static propTypes = {
    amp: PropTypes.bool,
    name: PropTypes.string.isRequired,
    imageUrl: PropTypes.string.isRequired,
    productUrl: PropTypes.string.isRequired,
    offers: PropTypes.arrayOf(
      PropTypes.shape({
        price: PropTypes.string,
        externalUrl: PropTypes.string,
        prices: PropTypes.arrayOf(
          PropTypes.shape({
            price: PropTypes.string,
            type: PropTypes.string,
          }),
        ),
      }),
    ).isRequired,
    description: DescriptionPropType,
    size: PropTypes.string,
    articlePublishDate: PropTypes.string.isRequired,
    promotionalMedia: PropTypes.arrayOf(
      PropTypes.shape({
        url: PropTypes.shape({ primary: PropTypes.string }),
      }),
    ),
    vertical: PropTypes.string.isRequired,
  };

  static contextTypes = {
    canonicalUrl: PropTypes.string,
  };

  static defaultProps = {
    amp: false,
    description: {
      primary: '',
      seo: '',
      tease: '',
    },
    size: 'medium',
    promotionalMedia: [],
  };

  constructor(props) {
    super(props);

    const { imageUrl } = this.props;

    this.state = {
      currentMedia: imageUrl,
      currSlideIdx: 0,
    };
  }

  changeCurrentMedia = (currentMedia, currSlideIdx) => this.setState(
    {
      currentMedia,
      currSlideIdx,
    },
  );

  picture = (mediaUrl) => {
    const {
      size,
      amp,
      vertical,
      productUrl,
    } = this.props;
    const { currentMedia } = this.state;

    const isLarge = size === 'large' && !amp;

    const sizes = isLarge
      ? {
        s: AIMS_FLAVORS.FOCAL_560x560,
      }
      : {
        s: AIMS_FLAVORS.FIT_720w,
        m: AIMS_FLAVORS.FIT_260w,
      };
    const flavor = isLarge ? 'focal' : 'fit';

    const bemElemPictureLink = `${block}__pictureLink`;
    const pictureLinkClasses = classNames(
      bemElemPictureLink,
      'listicle-product__picture-link',
      {
        db: !mediaUrl && !isLarge,
        'db-m dn': !mediaUrl && isLarge,
      },
    );

    return (
      <div className={pictureLinkClasses}>
        <a
          className="w-100"
          href={isTelemundoVertical(vertical) ? productUrl : this.affiliateUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Picture
            url={mediaUrl || currentMedia}
            flavor={flavor}
            responsiveFlavors={sizes}
          />
        </a>
      </div>
    );
  }

  mobileMediaSlide = () => {
    const { promotionalMedia } = this.props;
    const { currSlideIdx } = this.state;

    return (
      <div className="listicle-product__mobile-media">
        <Carousel
          afterSlide={(nextIdx) => this.setState({ currSlideIdx: nextIdx })}
          decorators={[]}
          slideIndex={currSlideIdx}
          withoutControls
        >
          {promotionalMedia.map(({ url }) => (
            <div className="listicle-product__mobile-media-item" key={url.primary}>
              {this.picture(url.primary)}
            </div>
          ))}
        </Carousel>
      </div>
    );
  };

  renderOffers = () => {
    const {
      offers, name, articlePublishDate, vertical,
    } = this.props;

    const { price } = offers[0] || {};

    return offers && offers.length && offers.map((offer) => {
      if (!offer) {
        return null;
      }

      const vendorUrl = offer.externalUrl;
      const offerPrices = offer.prices && offer.prices.length
        ? findPrices(offer)
        : null;
      const salePrice = offerPrices && offerPrices.sale;
      // eslint-disable-next-line max-len
      const listPrice = isTelemundoVertical(vertical) ? offer.prices[0].price : offerPrices && offerPrices.list;
      const seller = offer.seller && offer.seller.name;

      return (
        <ShopButtonInfo
          key={vendorUrl}
          articlePublishDate={articlePublishDate}
          name={name}
          vertical={vertical}
          salePrice={salePrice}
          listPrice={listPrice || price}
          seller={isTelemundoVertical(vertical) ? 'Comprar' : seller}
          productUrl={vendorUrl}
          additionalStyles={{
            styles,
            vendor: 'listlicle-product__vendor',
            cost: 'listlicle-product__cost',
            shop: 'listlicle-product__button',
          }}
        />
      );
    });
  }

  renderUniCheckoutSeller = (vertical, offers) => (
    isTelemundoVertical(vertical) && offers && offers[0].seller
      ? (
        <p className="listicle-product__seller-name">
          Vendido por&nbsp;
          {offers[0].seller.name}
        </p>
      )
      : null
  )

  render() {
    const {
      name,
      description,
      size,
      promotionalMedia,
      amp,
      offers,
      articlePublishDate,
      vertical,
    } = this.props;
    let { productUrl } = this.props;
    const { canonicalUrl } = this.context;
    const { currSlideIdx } = this.state;

    productUrl = isTelemundoVertical(vertical) && offers ? offers[0].externalUrl : productUrl;

    const fetchedEncoderStopDate = getDateTime(ENCODER_STOP_DATE);
    const fetchedCurrentArticlePublishDate = getDateTime(articlePublishDate);
    // eslint-disable-next-line max-len
    const isCurrentArticlePublishedAfterEncoderStopDate = fetchedCurrentArticlePublishDate > fetchedEncoderStopDate;
    this.affiliateUrl = isCurrentArticlePublishedAfterEncoderStopDate
      ? productUrl
      : buildAffiliateLink(productUrl, canonicalUrl);

    const bemElemProductTitleLink = `${block}__productTitleLink`;

    const { primary: primaryDescription } = description || {};

    const isLarge = size === 'large' && !amp;
    const hasPromotionalMedia = !!promotionalMedia.length && isLarge;

    return (
      <>
        <ListicleProductContainer isLarge={isLarge}>
          <div className={classNames('listicle-product__border', { 'df flex-column flex-row-m': isLarge })}>
            {/* Promotional Media for tablet and up */}
            { hasPromotionalMedia && (
              <PromotionalMedia
                media={promotionalMedia}
                selectMedia={this.changeCurrentMedia}
                additionalStyles={{
                  container: 'dn df-m flex-column w-4-m flex-grow-0 flex-shrink-0',
                  item: 'mb2',
                }}
              />
            )}
            <div
              className={classNames('df flex-column items-stretch listicle-product__card-container',
                {
                  'flex-row-l flex-grow-1 pb6-l': isLarge,
                  'flex-row-m pb6-m': !isLarge,
                })}
            >
              { this.picture() }
              { isLarge && this.mobileMediaSlide() }
              <div className={classNames(`${block}__productCardInfo pt5 pb5 flex-grow-1`, { 'pt0-m pb0-m': !isLarge, 'pb6-m pb0-l': isLarge }, 'listicle-product__card-info')}>
                {/* Promotional Media for mobile */}
                { hasPromotionalMedia && (
                  <PromotionalMedia
                    media={promotionalMedia}
                    selectMedia={this.changeCurrentMedia}
                    selectedIdx={currSlideIdx}
                    isMobile
                    additionalStyles={{
                      container: 'dn-m df mb7',
                    }}
                  />
                )}

                { this.renderUniCheckoutSeller(vertical, offers) }

                <a
                  className={classNames(bemElemProductTitleLink)}
                  href={isTelemundoVertical(vertical) ? productUrl : this.affiliateUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <h2 className="listicle-product__headline">{name}</h2>
                  { primaryDescription && (
                    <p
                      data-test="listicle__description"
                      className={classNames('listicle-product__description', {
                        'gray-100': isLarge,
                        'gray-80': !isLarge,
                      })}
                    >
                      {primaryDescription}
                    </p>
                  )}
                </a>
                { this.renderOffers() }
              </div>
            </div>
          </div>
        </ListicleProductContainer>
      </>
    );
  }
}

export default ListicleProduct;
